// public/v3/src/lib/careplan-pain.js — program-aware CPM prompt extension
// for the unified care-plan generator (compliance plan P2.1).
//
// Care-plan generation is unified per (patient, month) regardless of
// program. When a patient is pain-enrolled (G3002), an additional
// <pain_section> block is appended to the assembled CCM prompt so the AI
// produces a person-centered Chronic Pain Management Care Planning section
// (goals, strengths, desired outcomes, treatment elements) alongside the
// chronic-condition sections.
//
// Lives separately from careplan-generate.js so Jest can require this
// helper directly (careplan-generate.js is ESM with browser imports and
// can't be Jest-loaded). careplan-generate.js imports from this module
// via ESM interop, same pattern as careplan-bhi.js.
//
// Module format: CommonJS (module.exports). Pure functions only — no React,
// no fetches.
//
// Grounding rules mirror the billing-note attestation (pain-billing.js):
// every data line is omit-if-absent; the medical-necessity and
// excluded-treatments texts reach the prompt ONLY when a stored confirmer
// exists (an unconfirmed draft must never inform a compliance document);
// delivery modes are counted only from the stored 'in-person'/'telehealth'
// literals, never inferred.

const { isPainEnrolledPatient } = require('./pain-billing');
const { entryProgramFamily } = require('./bhi-billing');
const {
  painAssessmentsForMonth,
  isValidPainAssessment,
  computePainSeverity,
  lookupPainScale,
} = require('./pain-scales');

// Free text that reaches the prompt (activity types/notes, necessity,
// exclusions) is neutralized at render time so stored text can never close
// </pain_section> or open a new tag and impersonate prompt structure
// (adversarial review 2026-07-21, findings 1-2). Semantics unchanged from
// that review's fix — now shared via prompt-text.js.
const { stripPromptAngles: _promptSafe } = require('./prompt-text');

function entryActivityType(entry) {
  const type = _promptSafe((entry && (entry.type || entry.label || entry.desc)) || '', 80);
  return type || 'pain management activity';
}

function entryActivityNote(entry) {
  const e = entry || {};
  const note = _promptSafe(e.notes || e.description || e.desc || e.hint || '', 200);
  const type = entryActivityType(e);
  const label = _promptSafe(e.label || '', 80);
  if (!note || note === type || note === label) return '';
  return note;
}

function _monthPainEntries(patient, monthStr) {
  const log = Array.isArray(patient && patient.activityLog) ? patient.activityLog : [];
  const month = String(monthStr || '');
  return log
    .filter((e) => e && !e.isConsolidatedNote && (!month || String(e.date || '').startsWith(month)))
    .filter((e) => entryProgramFamily(e) === 'pain');
}

function buildPainActivityLines(patient, monthStr) {
  const entries = _monthPainEntries(patient, monthStr)
    .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
  if (!entries.length) return '(no pain-management activity-log entries supplied for this month)';
  return entries.slice(0, 8).map((e) => {
    const date = e.date || 'undated';
    const duration = parseInt(e.duration, 10) || 0;
    const note = entryActivityNote(e);
    return `- ${date}: ${duration} min ${entryActivityType(e)}${note ? ` — ${note}` : ''}`;
  }).join('\n');
}

// A stored text block (necessity / excludedTreatments) informs the prompt
// only when an identified confirmer recorded it — same rule the billing
// note applies (pain-billing.js buildPainAttestationLines).
function _confirmedText(block) {
  if (!block || !block.text || !block.confirmedBy || !block.confirmedBy.name) return '';
  return _promptSafe(block.text);
}

function buildPainPromptSection(patient, monthStr) {
  if (!patient || !isPainEnrolledPatient(patient)) return '';
  const pain = patient.pain || {};

  // Capped like the activity lines: a pathological chart must not balloon
  // the prompt (typical months hold one or two assessments). Date must also
  // be a real calendar date — isValidPainAssessment only requires truthy,
  // and a month-prefix match would present '2026-07-99' as "validated"
  // (adversarial review finding 5).
  const validAssessments = painAssessmentsForMonth(patient, monthStr)
    .filter(isValidPainAssessment)
    .filter((a) => !Number.isNaN(Date.parse(a.date)))
    .slice(0, 6);
  const assessmentLine = validAssessments.length
    ? validAssessments.map((a) => {
        const scale = lookupPainScale(a.scale);
        const severity = computePainSeverity(a.scale, Number(a.score));
        return `${a.scale} score ${a.score}/${scale.maxScore}${severity ? ` (${severity})` : ''} on ${a.date}`;
      }).join('; ')
    : '(no validated pain assessment supplied for this month)';

  const lines = [`- Validated pain assessments this month: ${assessmentLine}`];

  const necessity = _confirmedText(pain.necessity);
  if (necessity) lines.push(`- Documented medical necessity: ${necessity}`);

  if (pain.consent && pain.consent.durationOver3Months === true) {
    lines.push('- Chronic pain persisting, or expected to persist, longer than 3 months (attested at enrollment).');
  }

  // Modes are counted only from stored literals; entries without a stored
  // mode are unspecified and never counted (pain-billing modality rule).
  const modality = { telehealth: 0, 'in-person': 0 };
  for (const e of _monthPainEntries(patient, monthStr)) {
    const mode = e && (e.mode === 'in-person' || e.mode === 'telehealth') ? e.mode : '';
    if (mode) modality[mode] += 1;
  }
  if (modality.telehealth || modality['in-person']) {
    const parts = [];
    if (modality['in-person']) parts.push(`${modality['in-person']} in-person`);
    if (modality.telehealth) parts.push(`${modality.telehealth} telehealth`);
    lines.push(`- Delivery modality of documented encounters this month: ${parts.join(', ')}.`);
  }

  lines.push('- Documented pain-management activity this month:');
  lines.push(buildPainActivityLines(patient, monthStr));

  const excluded = _confirmedText(pain.excludedTreatments);
  // "recorded rationale", never "the provider's": confirmedBy is the acting
  // user who reviewed the text in the dialog, not necessarily the designated
  // billing provider (adversarial review finding 8).
  const exclusionInstr = excluded
    ? `- Treatment elements deliberately excluded from this plan, with the recorded rationale (present these as documented exclusions, grounded verbatim in this record): ${excluded}`
    : '- No excluded-treatments record is stored: do NOT fabricate a list of excluded or considered-and-rejected treatments, and do NOT add a "none documented" or "no exclusions" statement.';

  // A pain+BHI patient with no CCM/APCM base must not receive two sections
  // each claiming sole focus (adversarial review finding 10); the base-CCM
  // scaffolding for base-less enrollments remains the documented careplan-bhi
  // known limitation.
  const crossRef = patient.billing && patient.billing.program
    ? 'Note that this is a comprehensive care plan covering chronic care management AND chronic pain management; reference both contexts.'
    : patient.billing && patient.billing.bhiProgram
    ? 'Note that this care plan covers behavioral health integration AND chronic pain management; reference both contexts.'
    : 'This care plan focuses on chronic pain management.';

  return `

<pain_section>
The patient is enrolled in Chronic Pain Management (HCPCS G3002). Include a person-centered Chronic Pain Management Care Planning section in the care plan covering the patient's goals, strengths, desired outcomes, and the treatment elements included in the plan, built from the documented data below:
${lines.join('\n')}
${exclusionInstr}
- Use only the pain data lines above and chart data already supplied in the main prompt. Do NOT state that interventions, medication changes, referrals, specialist coordination, or treatment-plan revisions occurred unless those actions are explicitly documented in those sources.
- Do NOT present goals, strengths, or preferences as stated by the patient unless the chart documents them; otherwise frame them as proposed by the care team for discussion with the patient.
- Absence of supplied data is not evidence of absence: do NOT state that no assessment was performed or that no pain-management activity occurred; when a data line above is an empty-state placeholder, simply omit that topic.
- If only enrollment and assessment data are supplied, describe assessment-informed goals and monitoring needs only; do NOT add a "none documented" statement about interventions or coordination.
${crossRef}
</pain_section>
`;
}

module.exports = {
  buildPainPromptSection,
  buildPainActivityLines,
};
